
import React from "react";
import { Copy, Mail, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Message, MessageTone } from "@/types/message";
import RegenerateButton from "./RegenerateButton";

interface MessageActionsProps {
  message?: Message;
  content?: string;
  companyName?: string;
  onRegenerateAIResponse?: (messageId: string, tone: MessageTone, customInstructions?: string) => Promise<Message | null>;
  isRegenerating?: boolean;
  userEmail?: string;
  sellerEmail?: string;
  orderNumber?: string;
  conversationId?: string;
}

const MessageActions = ({
  message,
  content,
  companyName,
  onRegenerateAIResponse,
  isRegenerating,
  userEmail = "user@example.com",
  sellerEmail = "support@company.com",
  orderNumber = "ORDER123",
  conversationId
}: MessageActionsProps) => {
  const { toast } = useToast();

  // Use content from message if available, otherwise use direct content prop
  const messageContent = message?.content || content || "";

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(messageContent);
      toast({
        title: "Copied to clipboard",
        description: "The message has been copied to your clipboard.",
      });
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      toast({
        variant: "destructive",
        title: "Copy failed",
        description: "Failed to copy the message to clipboard.",
      });
    }
  };

  const handleOpenInEmail = () => {
    const subject = encodeURIComponent(`Refund Request - Order ${orderNumber}`);
    const body = encodeURIComponent(messageContent);
    const mailto = `mailto:${sellerEmail}?subject=${subject}&body=${body}`;
    window.open(mailto, '_blank');
  };

  const handleBugReport = () => {
    const subject = encodeURIComponent('Bug Report - SnapRefund');
    const body = encodeURIComponent(`Bug Report Details:

Conversation ID: ${conversationId || 'N/A'}
Message ID: ${message?.id || 'N/A'}
Timestamp: ${new Date().toISOString()}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}

Issue Description:
[Please describe the issue you encountered]

Steps to Reproduce:
1. 
2. 
3. 

Expected Behavior:
[What you expected to happen]

Actual Behavior:
[What actually happened]
`);
    const mailto = `mailto:contact@snaprefund.com?subject=${subject}&body=${body}`;
    window.open(mailto, '_blank');
  };

  return (
    <div className="flex flex-wrap gap-2 pt-3 border-t">
      <Button
        variant="outline"
        size="sm"
        onClick={handleCopyToClipboard}
        className="text-blue-900 hover:text-blue-800 hover:bg-blue-50 border-blue-200"
      >
        <Copy className="h-4 w-4" style={{ marginRight: '2px' }} />
        Copy to Clipboard
      </Button>

      <Button
        variant="outline"
        size="sm"
        onClick={handleOpenInEmail}
        className="text-teal-600 hover:text-teal-700 hover:bg-teal-50 border-teal-200"
      >
        <Mail className="h-4 w-4" style={{ marginRight: '2px' }} />
        Open in Email
      </Button>

      {message?.type === "ai" && onRegenerateAIResponse && message?.id && (
        <RegenerateButton
          onClick={() => onRegenerateAIResponse(message.id, "polite")}
          isRegenerating={isRegenerating || false}
        />
      )}

      <Button 
        variant="outline" 
        size="sm" 
        className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
        onClick={handleBugReport}
      >
        <AlertTriangle className="h-4 w-4" style={{ marginRight: '2px' }} />
        Report a Bug
      </Button>
    </div>
  );
};

export default MessageActions;
